import type { CityConfig } from "../city-template/types";

export const westbrookConfig: CityConfig = {
  city: "Westbrook",
  state: "Maine",
  stateAbbr: "ME",
  slug: "westbrook",
  route: "/cities/westbrook",
  title: "Emergency & Fast Passport Renewal Westbrook ME | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Westbrook, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Westbrook, Portland, Gorham, and Windham travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Westbrook, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Westbrook and Cumberland County travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Westbrook%20Maine%20small%20city%20with%20Presumpscot%20River%2C%20beautiful%20historic%20downtown%20with%20brick%20buildings%20along%20tree-lined%20streets%2C%20New%20England%20autumn%20colors%2C%20warm%20afternoon%20light%2C%20professional%20photography%2C%20no%20visible%20text%20or%20people%2C%20clean%20scenic%20river%20and%20downtown%20view%20near%20Portland%20Maine&width=1800&height=700&seq=westbrook-hero-001&orientation=landscape",
  heroImageAlt: "Westbrook Maine small city with Presumpscot River and historic downtown",
  geoPosition: "43.6770;-70.3709",
  geoPlacename: "Westbrook, Maine",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 115 miles (2 hours) from Westbrook, ME",
  },
  nearbyCitiesZips: [
    { name: "Downtown Westbrook", zips: "04092" },
    { name: "Portland", zips: "04101" },
    { name: "Gorham", zips: "04038" },
    { name: "Windham", zips: "04062" },
    { name: "South Portland", zips: "04106" },
    { name: "Scarborough", zips: "04074" },
    { name: "Falmouth", zips: "04105" },
    { name: "Raymond", zips: "04071" },
    { name: "Standish", zips: "04084" },
    { name: "Buxton", zips: "04093" },
    { name: "Cumberland", zips: "04021" },
    { name: "Yarmouth", zips: "04096" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Westbrook departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Portland", route: "/cities/portland-me" },
    { name: "Emergency passport help in South Portland", route: "/cities/south-portland" },
    { name: "Urgent passport services in Biddeford", route: "/cities/biddeford" },
    { name: "Same-day passport help in Saco", route: "/cities/saco" },
    { name: "Expedited passport services in Augusta", route: "/cities/augusta-me" },
    { name: "Passport courier services in Bangor", route: "/cities/bangor" },
    { name: "Rush passport help in Lewiston", route: "/cities/lewiston" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Westbrook, Maine remotely via secure courier. We do not have a walk-in office in Westbrook — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Westbrook travelers.",
  fpcOfficeDistance: "Serving western Cumberland County",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Westbrook and the western Cumberland County area. From downtown Westbrook along the Presumpscot River to the suburbs of Gorham and Windham — we help Westbrook travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all Westbrook neighborhoods and nearby cities including Portland, Gorham, Windham, South Portland, Scarborough, and Falmouth. If you are in Cumberland County, we have you covered.",
  howItWorksIntro: "Most Westbrook travelers complete everything online and ship documents overnight. No need to drive to Boston or wait in line at the regional agency. Our emergency and expedited passport process is designed for Maine travelers who need fast, reliable results.",
  mistakesIntro: "Westbrook's proximity to Portland International Jetport means travelers need their passports ready on time. Here are the most common mistakes we see.",
  testimonialsIntro: "Westbrook travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Westbrook? Here are the most common ones we hear from Maine travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Westbrook international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Laura D.",
      location: "Westbrook, ME",
      rating: 5,
      text: "My flight to London was in 3 days and my passport expired last year. Fast Passport Center processed my renewal in under 48 hours. I shipped from the Westbrook UPS Store and it was incredibly easy.",
      service: "Emergency Passport",
    },
    {
      name: "Kevin J.",
      location: "Gorham, ME",
      rating: 5,
      text: "Needed to renew my passport for a business trip to Amsterdam. Fast Passport Center caught a photo issue before submission — saved me from a rejection. Had my passport in 4 days.",
      service: "Passport Renewal",
    },
    {
      name: "Susan M.",
      location: "Windham, ME",
      rating: 5,
      text: "Lost my passport just before a summer trip to France. Fast Passport Center handled the DS-64 form and got me a replacement in 2 days. Incredible service for the Windham area.",
      service: "Lost Passport",
    },
    {
      name: "Paul T.",
      location: "Westbrook, ME",
      rating: 5,
      text: "I commute to Portland International Jetport for frequent international business travel. Fast Passport Center has always had my passport ready well ahead of schedule. Dependable and professional.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "westbrook-1",
      name: "Westbrook Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1041 Main St",
      city: "Westbrook",
      state: "ME",
      zip: "04092",
      phone: "(207) 854-2811",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Westbrook",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "westbrook-2",
      name: "Portland Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "125 Forest Ave",
      city: "Portland",
      state: "ME",
      zip: "04101",
      phone: "(207) 871-8391",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "4 miles from downtown Westbrook",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Westbrook residents visiting Portland. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "westbrook-3",
      name: "Gorham Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "35 Main St",
      city: "Gorham",
      state: "ME",
      zip: "04038",
      phone: "(207) 839-4131",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "5 miles from downtown Westbrook",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Good option for Gorham and Standish residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "westbrook-4",
      name: "Windham Town Office",
      type: "Town Office Passport Acceptance",
      address: "8 School Rd",
      city: "Windham",
      state: "ME",
      zip: "04062",
      phone: "(207) 892-1900",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "8 miles from downtown Westbrook",
      website: "https://www.windhamme.gov/",
      websiteLabel: "Windham Town Office",
      notes: "Accepts walk-ins but appointments preferred. Photo services not available on-site.",
      icon: "ri-government-line",
    },
    {
      id: "westbrook-5",
      name: "Scarborough Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "37 Pleasant Hill Rd",
      city: "Scarborough",
      state: "ME",
      zip: "04074",
      phone: "(207) 883-5124",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "8 miles from downtown Westbrook",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for Scarborough and South Portland residents. Appointment recommended.",
      icon: "ri-mail-line",
    },
    {
      id: "westbrook-6",
      name: "Falmouth Town Office",
      type: "Town Office Passport Acceptance",
      address: "271 Falmouth Rd",
      city: "Falmouth",
      state: "ME",
      zip: "04105",
      phone: "(207) 781-5253",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "7 miles from downtown Westbrook",
      website: "https://www.falmouthme.org/",
      websiteLabel: "Falmouth Town Office",
      notes: "Small town office with limited hours. Call ahead to schedule passport acceptance.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Westbrook acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Westbrook applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Westbrook families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Westbrook travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Westbrook." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Westbrook address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Westbrook, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Westbrook in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Westbrook travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Westbrook, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Westbrook residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Westbrook, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Westbrook with no office visit required. We serve all Westbrook neighborhoods and the surrounding Cumberland County area. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Westbrook, Maine?",
      a: "The fastest way to get an urgent passport in Westbrook is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing (8–11 weeks) or even government expedited mail (2–3 weeks).",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Westbrook?",
      a: "Popular options include the Westbrook Post Office (04092), Portland Main Post Office (04101), Gorham Post Office (04038), and Windham Town Office (04062). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Gorham, Windham, or Scarborough?",
      a: "We serve all of Cumberland County including Gorham, Windham, Scarborough, Portland, South Portland, and Falmouth. Our overnight shipping labels work from any UPS location, so your location doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Westbrook?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed — emergency 24–48 hour processing, 3–5 day expedited, or standard options. Contact us for a personalized quote based on your timeline.",
    },
    {
      q: "Do you help with passport photos in Westbrook?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS, Walgreens, and UPS Store locations in Westbrook and Portland offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Westbrook?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Westbrook?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Westbrook families through the entire process and help schedule acceptance clerk appointments.",
    },
    {
      q: "How close is Westbrook to Portland International Jetport?",
      a: "Portland International Jetport (PWM) is just 5 miles from downtown Westbrook — about a 10-minute drive. This makes Westbrook one of the most convenient locations for air travelers in Maine. Fast Passport Center helps ensure your passport is ready before you head to the airport.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Westbrook", address: "1 Riverbank Rd, Westbrook, ME 04092", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Westbrook", address: "679 Main St, Westbrook, ME 04092", hours: "Mon–Sun: 7AM–11PM" },
    { name: "UPS Store — Westbrook", address: "1041 Main St, Westbrook, ME 04092", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Westbrook", address: "451 Payne Rd, Scarborough, ME 04074", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Gorham", address: "35 Main St, Gorham, ME 04038", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Windham", address: "731 Roosevelt Trail, Windham, ME 04062", hours: "Mon–Sun: 7AM–10PM" },
    { name: "CVS Pharmacy — South Portland", address: "799 Broadway, South Portland, ME 04106", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Portland", address: "425 Congress St, Portland, ME 04101", hours: "Mon–Sun: 7AM–11PM" },
  ],
  dmvLocations: [
    {
      name: "BMV — Westbrook / Portland",
      address: "36 Presumpscot St, Portland, ME 04103",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "BMV — Gorham",
      address: "35 Main St, Gorham, ME 04038",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Passport-ready photo services available. Online appointment booking.",
      featured: false,
    },
    {
      name: "BMV — Windham",
      address: "8 School Rd, Windham, ME 04062",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID processing. Walk-ins limited — book online first.",
      featured: false,
    },
    {
      name: "BMV — Scarborough",
      address: "500 Route 1, Scarborough, ME 04074",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center. Online appointment system available.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "5 miles from Westbrook (10 minutes)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport is practically in Westbrook's backyard — just a 10-minute drive. Direct flights to Canada, Florida, and major U.S. hubs including international connections through Boston and NYC.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: true,
    },
    {
      code: "BOS",
      name: "Logan International Airport",
      distance: "115 miles from Westbrook (2 hours)",
      address: "1 Harborside Dr, Boston, MA 02128",
      note: "Major international hub with direct flights to Europe, Asia, and Latin America. Many Westbrook travelers connect through Boston for international travel.",
      flightUrl: "https://www.massport.com/logan-airport",
      flightLabel: "Logan Airport",
      featured: false,
    },
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "135 miles from Westbrook (2.5 hours)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Maine's second busiest airport. Alternative for northern destinations or when Portland is overbooked.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: false,
    },
  ],
};